<template>
  <div class="flex flex-col gap-1">
    <div v-if="broadcastLiveState" class="flex flex-col items-center">
      <div>
        <div class="flex flex-col sm:text-xl md:flex-row md:gap-1">
          <div>
            <translation
              v-if="humanDurationToNextMessage"
              keypath="ngp/interactivity_lock/broadcast_progress/next_message_at/text"
            >
              <template #nextMessageTimeout>
                <span class="bg-gp-charcoal-grey whitespace-nowrap px-1 py-0.5 text-base">{{
                  humanDurationToNextMessage
                }}</span>
              </template>
            </translation>
            <span v-else>{{ t("ngp/interactivity_lock/broadcast_progress/all_messages_sent/text") }}</span>
          </div>
          <div>
            <translation
              v-if="humanDurationToBroadcastEnd"
              keypath="ngp/interactivity_lock/broadcast_progress/broadcast_end/text"
            >
              <template #broadcastEndTimeout>
                <span class="bg-gp-charcoal-grey whitespace-nowrap px-1 py-0.5 text-base">{{
                  humanDurationToBroadcastEnd
                }}</span>
              </template>
            </translation>
          </div>
        </div>
      </div>
      <gp-progress-fancy v-if="broadcastProgress" class="w-full xl:w-2/3" :hide-label="true" :value="broadcastProgress">
      </gp-progress-fancy>
    </div>
    <div v-if="broadcastLiveState">
      <gp-btn
        size="small"
        :label="t('ngp/action/skip_broadcast/title')"
        :icon="faForward"
        :disabled="broadcastSkipped"
        @click="skipBroadcast"
      ></gp-btn>
    </div>
  </div>
</template>
<script setup lang="ts">
import { faForward } from "@fortawesome/pro-solid-svg-icons";
import { GpBtn, GpProgressFancy } from "@gportal/uikit";
import { computed, ref, watch } from "vue";
import { Translation, useI18n } from "vue-i18n";
import type { BroadcastProgress, Maybe } from "../../../graphql";
import { useClientStore } from "../../../store/client";
import { useServiceStore } from "../../../store/service";
import { useTime } from "../../../util/time";

const { t } = useI18n();
const { humanDuration } = useTime();

const service = useServiceStore();
const client = useClientStore();
const currentState = computed(() => service.ctx.service?.currentState);
const broadcastLiveState = computed(() => currentState.value?.fsmStateLiveProgress as Maybe<BroadcastProgress>);

/**
 * Calculate the human difference of the current server time to the given timestamp. Returns `undefined` if the diff is
 * negative.
 * @param timestamp Timestamp in seconds
 */
function humanDurationFromServerTime(timestamp?: number) {
  if (!timestamp) return undefined;
  const diff = timestamp * 1000 - client.serverTime;
  if (diff < 0) return undefined;
  return humanDuration(diff);
}

const humanDurationToNextMessage = computed(() => humanDurationFromServerTime(broadcastLiveState.value?.nextMessageAt));
const humanDurationToBroadcastEnd = computed(() => humanDurationFromServerTime(broadcastLiveState.value?.stateExitAt));

const broadcastProgress = computed(() => {
  const lastStateChange = currentState.value?.fsmLastStateChange;
  const stateExitAt = broadcastLiveState.value?.stateExitAt;
  const time = client.serverTime;
  if (stateExitAt && lastStateChange) {
    return ((time / 1000 - lastStateChange) / (stateExitAt - lastStateChange)) * 100;
  }
  return undefined;
});

// Disable the broadcast skip button once the request has been put in to skip the broadcast
const broadcastSkipped = ref(false);

function skipBroadcast() {
  broadcastSkipped.value = true;
  service.skipBroadcast();
}

// Re-enable it if the FSM state changed so we are not dependent on this component being re-mounted
watch(
  () => currentState.value?.fsmState,
  () => (broadcastSkipped.value = false),
);
</script>
